// Pure, axios-free inventory-status helpers extracted from InventoryPage.tsx. These centralise the
// stock-status thresholds, the severity rank used for sorting, the status-driven accent colours, and
// the row comparator so the highest-risk (customer-facing) stock math is unit-testable in isolation.
//
// NOTE: the logic here is COPIED from InventoryPage.tsx (lines ~60-76 statusAccent/statusRank,
// ~146-176 compare/filter). InventoryPage is owned by a parallel workflow and is intentionally NOT
// rewired here — a follow-up can swap its inline copies for these exports once that workflow lands.
// Thresholds are identical: out = qty 0, low = qty <= 20, in = qty > 20.

// Discrete stock status derived purely from quantity.
export type InventoryStatus = 'out' | 'low' | 'in';

// Accent colour set shared by the left strip, status pill, and quantity text so one inventory row
// always reads a single colour. `main` is the saturated accent, `soft` the 12% tint used for pill
// backgrounds, `label` the human-readable status.
export type StatusAccent = { main: string; soft: string; label: string };

// Sort keys for the inventory card-row list. Map to: sku (string), quantity (number), status
// severity (out < low < in), and lastUpdated (date).
export type InventorySortKey = 'sku' | 'quantity' | 'status' | 'lastUpdated';
export type SortDir = 'asc' | 'desc';

// Minimal shape the comparator needs — any inventory-row-like object works.
export interface InventoryRowLike {
  skuId?: string;
  quantity: number;
  lastUpdated?: string;
}

// Classify a quantity into a discrete stock status (out = 0, low = <=20, in = >20).
export const statusOf = (quantity: number): InventoryStatus => {
  if (quantity === 0) return 'out';
  if (quantity <= 20) return 'low';
  return 'in';
};

// Rank status by severity for sorting (ascending = most urgent first): out (0) < low (1) < in (2).
// Reuses the same thresholds as statusOf — no new rule.
export const statusRank = (quantity: number): number => {
  if (quantity === 0) return 0;
  if (quantity <= 20) return 1;
  return 2;
};

// Status-driven accent set. Mirrors the getStockStatusLabel thresholds — purely presentational.
export const statusAccent = (quantity: number): StatusAccent => {
  if (quantity === 0) return { main: '#DC2626', soft: 'rgba(220,38,38,0.12)', label: 'Out of Stock' };
  if (quantity <= 20) return { main: '#D97706', soft: 'rgba(217,119,6,0.12)', label: 'Low Stock' };
  return { main: '#16A34A', soft: 'rgba(22,163,74,0.12)', label: 'In Stock' };
};

// Row comparator for the four sortable keys. Returns a stable, direction-aware comparison matching
// InventoryPage's inline `compare`. String keys use a locale/numeric-aware compare; quantity and
// status compare numerically; lastUpdated compares timestamps (invalid/empty dates sort as 0).
// Array sort is NOT guaranteed stable for ties by the comparator itself — callers that need stable
// ordering rely on the engine's stable sort (Node/V8 Array.prototype.sort is stable), so equal keys
// keep their input order.
export const compare = (
  a: InventoryRowLike,
  b: InventoryRowLike,
  key: InventorySortKey,
  dir: SortDir,
): number => {
  const mul = dir === 'asc' ? 1 : -1;
  switch (key) {
    case 'quantity':
      return (a.quantity - b.quantity) * mul;
    case 'status':
      return (statusRank(a.quantity) - statusRank(b.quantity)) * mul;
    case 'lastUpdated': {
      const at = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
      const bt = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
      return ((isNaN(at) ? 0 : at) - (isNaN(bt) ? 0 : bt)) * mul;
    }
    case 'sku':
    default:
      return (
        (a.skuId || '').localeCompare(b.skuId || '', undefined, { numeric: true, sensitivity: 'base' }) * mul
      );
  }
};

// Sort keys for the shelf-wise inventory list (ShelfInventoryViewer). Map to: skuCode (string),
// quantity (number), shelf = shelfLocation (string), warehouse = warehouseLocation (string), and
// lastUpdated (date, falling back to updatedAt then epoch 0).
export type ShelfSortKey = 'skuCode' | 'quantity' | 'shelf' | 'warehouse' | 'lastUpdated';

// Minimal shape the shelf comparator needs — any shelf-inventory-row-like object works.
export interface ShelfRowLike {
  skuCode?: string;
  quantity: number;
  shelfLocation?: string;
  warehouseLocation?: string;
  lastUpdated?: string;
  updatedAt?: string;
}

// Row comparator for the shelf-wise inventory list. Mirrors ShelfInventoryViewer's inline sortedData
// switch EXACTLY: string keys (skuCode/shelf/warehouse, with ''-fallback) use a locale/numeric-aware
// compare; quantity compares numerically; lastUpdated compares timestamps via new Date(lastUpdated ||
// updatedAt || 0). Equal keys keep their input order via the engine's stable sort (see `compare`).
export const compareShelf = (
  a: ShelfRowLike,
  b: ShelfRowLike,
  key: ShelfSortKey,
  dir: SortDir,
): number => {
  const mul = dir === 'asc' ? 1 : -1;
  const cmpStr = (x: string, y: string) =>
    x.localeCompare(y, undefined, { numeric: true, sensitivity: 'base' });
  switch (key) {
    case 'quantity':
      return (a.quantity - b.quantity) * mul;
    case 'shelf':
      return cmpStr(a.shelfLocation || '', b.shelfLocation || '') * mul;
    case 'warehouse':
      return cmpStr(a.warehouseLocation || '', b.warehouseLocation || '') * mul;
    case 'lastUpdated': {
      const at = new Date(a.lastUpdated || a.updatedAt || 0).getTime();
      const bt = new Date(b.lastUpdated || b.updatedAt || 0).getTime();
      return (at - bt) * mul;
    }
    case 'skuCode':
    default:
      return cmpStr(a.skuCode || '', b.skuCode || '') * mul;
  }
};
